import { useSearchParams } from 'react-router-dom';
import { PortalProvider } from './PortalContext';
import { PortalLayout } from './PortalLayout';
import { InvoiceList } from './InvoiceList';
import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

export const PortalPage = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';
    const [payingInvoiceId, setPayingInvoiceId] = useState<string | null>(null);

    const handlePayInvoice = async (invoiceId: string) => {
        setPayingInvoiceId(invoiceId);

        try {
            // Call the correct invoice checkout session edge function
            const { data, error } = await supabase.functions.invoke('create-invoice-checkout', {
                body: {
                    invoice_id: invoiceId,
                    // Proves entitlement for an anonymous caller (audit E-09).
                    token,
                    success_url: window.location.href + '&payment=success',
                    cancel_url: window.location.href + '&payment=cancelled'
                }
            });

            if (error) throw error;

            if (data?.checkout_url) {
                window.location.href = data.checkout_url;
            }
        } catch (err) {
            console.error('Payment error:', err);
            toast.error('Unable to process payment. Please try again or contact the business.');
        } finally {
            setPayingInvoiceId(null);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-canvas flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-card rounded-2xl border border-line p-8 text-center">
                    <h1 className="text-xl font-bold text-fg mb-2">Invalid Link</h1>
                    <p className="text-fg-3">
                        This link is missing required information. Please use the link sent to you by your service provider.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <PortalProvider token={token}>
            <PortalLayout>
                {/* Payment Loading Overlay */}
                {payingInvoiceId && (
                    <div className="fixed inset-0 bg-scrim/80 backdrop-blur-sm z-50 flex items-center justify-center">
                        <div className="bg-card rounded-xl p-8 text-center">
                            <Loader2 className="w-8 h-8 animate-spin text-success mx-auto mb-4" />
                            <p className="text-fg font-medium">Redirecting to payment...</p>
                        </div>
                    </div>
                )}

                {/* Main Content */}
                <div className="space-y-8">
                    {/* Welcome Section */}
                    <div className="bg-gradient-to-r from-card to-raised rounded-2xl border border-line-strong p-6">
                        <h2 className="text-lg font-bold text-fg mb-2">Welcome to your portal</h2>
                        <p className="text-fg-3 text-sm">
                            View your invoices, payment history, and make payments online.
                        </p>
                    </div>

                    {/* Invoice List */}
                    <InvoiceList onPayClick={handlePayInvoice} />
                </div>
            </PortalLayout>
        </PortalProvider>
    );
};

export default PortalPage;
